import { useState, useRef, useEffect, type ReactNode } from "react";
import styles from "./Dropdown.module.scss";
import { DropdownIcon } from "./assets/DropdownIcon";

interface Option {
  value: string;
  label: string;
  icon?: ReactNode;
}

interface DropdownProps {
  options: Option[];
  selectedValue: string;
  onSelect: (value: string) => void;
  label: string;
}

export function Dropdown({
  options,
  selectedValue,
  onSelect,
  label,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleSelect = (value: string) => {
    onSelect(value);
    setIsOpen(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selectedOption = options.find(
    (option) => option.value === selectedValue,
  );

  return (
    <div
      className={styles.dropdown}
      ref={dropdownRef}
      onClick={() => setIsOpen(!isOpen)}
    >
      <div>
        <div className={styles.labelWrap}>
          <div className={styles.label}>{label}</div>
          <div className={styles.selected}>
            {selectedOption?.icon}
            {selectedOption?.label}
          </div>
        </div>
        <DropdownIcon
          className={`${styles.icon} ${isOpen ? styles.open : ""}`}
        />
      </div>
      {isOpen && (
        <ul className={styles.options}>
          {options.map((option) => (
            <li
              key={option.value}
              className={`${styles.option} ${
                selectedValue === option.value ? styles.selectedOption : ""
              }`}
              onClick={() => handleSelect(option.value)}
            >
              {option.icon}
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
